import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IInvoiceNewVo } from '../interfaces/Invoice/iinvoice-new-vo';
import { IInvoiceSageProfileDto } from '../interfaces/Invoice/iinvoice-sage-profile-dto';
import { ISageBalanceForwardDto } from '../interfaces/Sage/isage-balance-forward-dto';
import { ISageCustomerDto } from '../interfaces/Sage/isage-customer-dto';
import { ISageCustomerSearchRequestDto } from '../interfaces/Sage/isage-customer-search-request-dto';
import { IResultObjVo } from '../interfaces/iresult-obj-vo';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  private ApiUrl = "";

  constructor(private http: HttpClient, @Inject('BASE_URL') baseUrl: string) {
    this.ApiUrl = baseUrl;
  }

  getKrollInvoice(pharmacyId, facility, patientId, startDate, endDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/GetKrollInvoice/' + pharmacyId + '/' + facility + '/' + patientId + '/' + startDate + '/' + endDate);
  }

  getInvoice(pharmacyId, facility, patientId, startDate, endDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/GetInvoice/' + pharmacyId + '/' + facility + '/' + patientId + '/' + startDate + '/' + endDate);
  }

  getResidents(pharmacyId, facility, startDate, endDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/getResidents/' + pharmacyId + '/' + facility + '/' + startDate + '/' + endDate);
  }

  getResidentsByMonth(pharmacyId, facility, startDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/getResidents/' + pharmacyId + '/' + facility + '/' + startDate);
  }

  getInvoicePatient(pharmacyId, facility, startDate, endDate, lastInvoiceNumber): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/GetInvoicePatient/' + pharmacyId + '/' + facility + '/' + startDate + '/' + endDate + '/' + lastInvoiceNumber);
  }

  public save(invoiceDto, userId): Observable<any> {
    const headers = { 'content-type': 'application/json' };
    const url = this.ApiUrl + 'api/invoice/save/' + userId;
    invoiceDto = {
      ...invoiceDto,
      invoiceReviewStatusId: 3,
      invoiceSageStatusId: 3,
      isDischargeOrDeceased: false,
     };
    const body = JSON.stringify(invoiceDto);

    return this.http.post<any>(url, invoiceDto, { 'headers': headers });
  }

  getInvoices(pharmacyId, facility, patientId, invoiceNumber): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/GetInvoices/' + pharmacyId + '/' + facility + '/' + patientId + '/' + invoiceNumber);
  }

  deleteInvoice(invoiceId, userId): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/DeleteInvoice/' + invoiceId + '/' + userId);
  }

  // new Invoices
  getNewKrollInvoice(pharmacyId, facility, patientId, startDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/NewGetKrollInvoice/' + pharmacyId + '/' + facility + '/' + patientId + '/' + startDate);
  }

  getNewPatientInvoice(invoiceId, pharmacyId, facility, patientId, startDate): Observable<any> {
    return this.http.get<any>(`${this.ApiUrl}api/invoice/GetNewPatientInvoice/${invoiceId}/${pharmacyId}/${facility}/${patientId}/${startDate}`);
  }

  getInvoiceById(invoiceId: number): Observable<IResultObjVo<IInvoiceNewVo>> {
    return this.http.get<IResultObjVo<IInvoiceNewVo>>(`${this.ApiUrl}api/invoice/GetInvoiceById/${invoiceId}`);
  }

  getNewInvoices(pharmacyId, facility, startDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/GetNewInvoices/' + pharmacyId + '/' + facility + '/' + startDate);
  }

  getNewInvoicesByFacilityWithKroll(pharmacyId, facility, startDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/GetNewInvoicesByFacilityWithKroll/' + pharmacyId + '/' + facility + '/' + startDate);
  }

  getNewInvoicesByFacilityWithoutKroll(pharmacyId, facility, startDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/GetNewInvoicesByFacilityWithoutKroll/' + pharmacyId + '/' + facility + '/' + startDate);
  }

  async sendUpdateSage(invoiceId, userId): Promise<any> {
    const url = `${this.ApiUrl}api/invoice/SendUpdateSage/${invoiceId}/${userId}`;
    return await this.http.get<any>(url).toPromise();
  }

  async saveNewInvoice(invoiceDto, userId): Promise<any> {
    const headers = { 'content-type': 'application/json' };
    const url = this.ApiUrl + 'api/invoice/saveorupdate/' + userId;
    const body = JSON.stringify(invoiceDto);

    return await this.http.post<any>(url, invoiceDto, { 'headers': headers }).toPromise();
  }

  async getProfileFromSage(pharmacyId, facility, patientId): Promise<IResultObjVo<string>> {
    const url = `${this.ApiUrl}api/invoice/GetProfileFromSage/${pharmacyId}/${facility}/${patientId}`;
    return await this.http.get<any>(url).toPromise();
  }

  getInvoiceSageProfile(pharmacyId, facility, patientId): Observable<IResultObjVo<IInvoiceSageProfileDto>> {
    return this.http.get<any>(`${this.ApiUrl}api/invoice/GetInvoiceSageProfile/${pharmacyId}/${facility}/${patientId}`);
  }

  getPatientBalanceForward(pharmacyId, facility, patientId): Observable<IResultObjVo<ISageBalanceForwardDto>> {
    return this.http.get<any>(`${this.ApiUrl}api/invoice/GetPatientBalanceForward/${pharmacyId}/${facility}/${patientId}`);
  }

  searchSageCustomer(request: ISageCustomerSearchRequestDto): Observable<ISageCustomerDto[]> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<ISageCustomerDto[]>(`${this.ApiUrl}api/invoice/SearchSageCustomer`, request, { headers });
  }

  getInvoicePaymentItemsByYear(pharmacyId, facility, patientId, year): Observable<IResultObjVo<any[]>> {
    return this.http.get<any>(`${this.ApiUrl}api/invoice/GetInvoicePaymentItemsByYear/${pharmacyId}/${facility}/${patientId}/${year}`);
  }

  reapplyNormalization(request: {
    invoiceId: number;
    pharmacyId: number;
    facilityId: number;
    patientId: number;
    startDate: string;
    normalizationOptions: any;
  }): Observable<IResultObjVo<IInvoiceNewVo>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<IInvoiceNewVo>>(
      `${this.ApiUrl}api/invoice/ReapplyNormalization`,
      request,
      { headers }
    );
  }

  createSalesInvoice(pharmacyId, facilityId, patientId, invoiceId): Observable<IResultObjVo<any>> {
    const header = { 'content-type': 'application/json' };
    const url = `${this.ApiUrl}api/invoice/CreateSalesInvoice/${pharmacyId}/${facilityId}/${patientId}/${invoiceId}`;
    return this.http.post<any>(url, {}, { 'headers': header });
  }

  updateSalesInvoice(pharmacyId, facilityId, patientId, invoiceId): Observable<IResultObjVo<any>> {
    const header = { 'content-type': 'application/json' };
    const url = `${this.ApiUrl}api/invoice/UpdateSalesInvoice/${pharmacyId}/${facilityId}/${patientId}/${invoiceId}`;
    return this.http.post<any>(url, {}, { 'headers': header });
  }

  getPatientInvoiceList(pharmacyId, facility, patientId): Observable<IResultObjVo<IInvoiceNewVo[]>> {
    return this.http.get<any>(`${this.ApiUrl}api/invoice/GetPatientInvoiceList/${pharmacyId}/${facility}/${patientId}`);
  }

  getPatientInvoiceListWithBalanceForward(pharmacyId, facility, patientId): Observable<IResultObjVo<IInvoiceNewVo[]>> {
    return this.http.get<any>(`${this.ApiUrl}api/invoice/GetPatientInvoiceListWithBalanceForward/${pharmacyId}/${facility}/${patientId}`);
  }

  updateSageName(invoiceProfileId: number, pharmacyId: number, facilityId: number, patientId: number, newSageName: string, canSendEmail: Boolean): Observable<any> {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.ApiUrl}api/invoice/UpdateSageName/${invoiceProfileId}/${pharmacyId}/${facilityId}/${patientId}`;

    return this.http.post<any>(url, {
      sageName: newSageName,
      canSendEmail
    }, { 'headers': headers });
  }

  updateSageProfile(invoiceProfileId: number, pharmacyId: number, facilityId: number, patientId: number, profileData: any): Observable<any> {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.ApiUrl}api/invoice/UpdateSageName/${invoiceProfileId}/${pharmacyId}/${facilityId}/${patientId}`;

    return this.http.post<any>(url, profileData, { 'headers': headers });
  }

  clearSageProfile(invoiceProfileId: number, pharmacyId: number, facilityId: number, patientId: number): Observable<IResultObjVo<string>> {
    const url = `${this.ApiUrl}api/invoice/ClearSageProfile/${invoiceProfileId}/${pharmacyId}/${facilityId}/${patientId}`;
    return this.http.post<IResultObjVo<string>>(url, {});
  }

  sendAllInvoicesByEmail(pharmacyId: number, facilityId :number,  invoiceIds: number[]): Observable<IResultObjVo<string>> {
    const headers = { 'content-type': 'application/json' };
    const url = this.ApiUrl + 'api/invoice/SendAllInvoicesByEmail';
    return this.http.post<IResultObjVo<string>>(url, {
      pharmacyId,
      facilityId,
      invoiceIds,
    }, {
      headers
    });
  }

  searchUserActionLogs(request: {
    pharmacyId?: number;
    facilityId?: number;
    patientId?: number;
    userId?: string;
    actionCode?: string;
    actionCodes?: string[];
    createdDateFrom?: string;
    createdDateTo?: string;
    maxRows?: number;
  }): Observable<any[]> {
    const headers = { 'content-type': 'application/json' };
    const url = this.ApiUrl + 'api/UserActionLog/Search';
    return this.http.post<any[]>(url, request, { headers: headers });
  }

  getPaymentVerifyLink(pharmacyId: number, facilityId: number, patientId: number): Observable<IResultObjVo<{ url: string; token: string; accessKey: string }>> {
    return this.http.get<IResultObjVo<{ url: string; token: string; accessKey: string }>>(
      this.ApiUrl + 'api/invoice/GetPaymentVerifyLink/' + pharmacyId + '/' + facilityId + '/' + patientId);
  }

  getPaymentVerifyLinkEmailOptions(pharmacyId: number, facilityId: number, patientId: number): Observable<IResultObjVo<{
    patientEmail: string;
    patientName: string;
    primaryRecipientLabel: string;
    allowPatientEmail: boolean;
    isProduction: boolean;
    accessKey: string;
    emailSubject: string;
    emailBody: string;
  }>> {
    return this.http.get<IResultObjVo<{
      patientEmail: string;
      patientName: string;
      primaryRecipientLabel: string;
      allowPatientEmail: boolean;
      isProduction: boolean;
      accessKey: string;
      emailSubject: string;
      emailBody: string;
    }>>(this.ApiUrl + 'api/invoice/GetPaymentVerifyLinkEmailOptions/' + pharmacyId + '/' + facilityId + '/' + patientId);
  }

  sendPaymentVerifyLinkEmail(request: {
    pharmacyId: number;
    facilityId: number;
    patientId: number;
    recipientMode: string;
    customEmail?: string;
    paymentMethodSetupLink?: string;
    performedByUserId?: string;
  }): Observable<IResultObjVo<any>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<any>>(
      this.ApiUrl + 'api/invoice/SendPaymentVerifyLinkEmail',
      request,
      { headers: headers });
  }

  getPaymentVerifyLinkSmsOptions(pharmacyId: number, facilityId: number, patientId: number): Observable<IResultObjVo<{
    phone1: string;
    phone2: string;
    allowPhone1: boolean;
    allowPhone2: boolean;
    isProduction: boolean;
    patientName: string;
    accessKey: string;
    smsBody: string;
    smsShortUrlReservedLength: number;
    smsMaxLength: number;
  }>> {
    return this.http.get<IResultObjVo<{
      phone1: string;
      phone2: string;
      allowPhone1: boolean;
      allowPhone2: boolean;
      isProduction: boolean;
      patientName: string;
      accessKey: string;
      smsBody: string;
      smsShortUrlReservedLength: number;
      smsMaxLength: number;
    }>>(this.ApiUrl + 'api/invoice/GetPaymentVerifyLinkSmsOptions/' + pharmacyId + '/' + facilityId + '/' + patientId);
  }

  sendPaymentVerifyLinkSms(request: {
    pharmacyId: number;
    facilityId: number;
    patientId: number;
    recipientMode: string;
    customPhone?: string;
    smsBody?: string;
    paymentMethodSetupLink?: string;
    performedByUserId?: string;
  }): Observable<IResultObjVo<any>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<any>>(
      this.ApiUrl + 'api/invoice/SendPaymentVerifyLinkSms',
      request,
      { headers: headers });
  }

  getPaymentVerifyLinkEmailBatchOptions(request: {
    pharmacyId: number;
    facilityId: number;
    patientIds: number[];
  }): Observable<IResultObjVo<{
    isProduction: boolean;
    primaryRecipientLabel: string;
    emailSubject: string;
    emailBody: string;
    recipients: Array<{
      patientId: number;
      firstName: string;
      lastName: string;
      listDisplayName: string;
      patientEmail: string;
      allowPatientEmail: boolean;
      accessKey: string;
      canSend: boolean;
      skipReason: string;
    }>;
  }>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<{
      isProduction: boolean;
      primaryRecipientLabel: string;
      emailSubject: string;
      emailBody: string;
      recipients: Array<{
        patientId: number;
        firstName: string;
        lastName: string;
        listDisplayName: string;
        patientEmail: string;
        allowPatientEmail: boolean;
        accessKey: string;
        canSend: boolean;
        skipReason: string;
      }>;
    }>>(this.ApiUrl + 'api/invoice/GetPaymentVerifyLinkEmailBatchOptions', request, { headers: headers });
  }

  getPaymentVerifyLinkSmsBatchOptions(request: {
    pharmacyId: number;
    facilityId: number;
    patientIds: number[];
  }): Observable<IResultObjVo<{
    isProduction: boolean;
    smsBody: string;
    smsMaxLength: number;
    recipients: Array<{
      patientId: number;
      firstName: string;
      lastName: string;
      listDisplayName: string;
      phone1: string;
      phone2: string;
      allowPhone1: boolean;
      allowPhone2: boolean;
      accessKey: string;
      canSend: boolean;
      skipReason: string;
    }>;
  }>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<{
      isProduction: boolean;
      smsBody: string;
      smsMaxLength: number;
      recipients: Array<{
        patientId: number;
        firstName: string;
        lastName: string;
        listDisplayName: string;
        phone1: string;
        phone2: string;
        allowPhone1: boolean;
        allowPhone2: boolean;
        accessKey: string;
        canSend: boolean;
        skipReason: string;
      }>;
    }>>(this.ApiUrl + 'api/invoice/GetPaymentVerifyLinkSmsBatchOptions', request, { headers: headers });
  }

  getPaymentSetupLinkEmailLastSent(request: {
    pharmacyId: number;
    facilityId: number;
    patientIds: number[];
  }): Observable<IResultObjVo<Array<{ patientId: number; lastEmailSentAt: string }>>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<Array<{ patientId: number; lastEmailSentAt: string }>>>(
      this.ApiUrl + 'api/invoice/GetPaymentSetupLinkEmailLastSent',
      request,
      { headers: headers });
  }

  getPaymentSetupLinkSmsLastSent(request: {
    pharmacyId: number;
    facilityId: number;
    patientIds: number[];
  }): Observable<IResultObjVo<Array<{ patientId: number; lastSmsSentAt: string }>>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<Array<{ patientId: number; lastSmsSentAt: string }>>>(
      this.ApiUrl + 'api/invoice/GetPaymentSetupLinkSmsLastSent',
      request,
      { headers: headers });
  }

  getPaymentMethodSetupCodes(request: {
    pharmacyId: number;
    facilityId: number;
    patientId: number;
  }): Observable<IResultObjVo<Array<{
    id: number;
    code: string;
    status: string;
    createdAt: string;
    expiresAt: string;
    updatedAt?: string;
    canceledAt?: string;
    completedAt?: string;
    validateLimit?: number;
    validateCount?: number;
    lastValidatedAt?: string;
    isUsed: boolean;
    isExpired: boolean;
    isCanceled: boolean;
    displayStatus: string;
    canCancel: boolean;
    paymentMethodSetupLink: string;
    sendChannel?: string;
    sendDestination?: string;
    sendActionCode?: string;
    sendUserId?: string;
    sendUserDisplayName?: string;
    sendAt?: string;
  }>>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<Array<{
      id: number;
      code: string;
      status: string;
      createdAt: string;
      expiresAt: string;
      updatedAt?: string;
      canceledAt?: string;
      completedAt?: string;
      validateLimit?: number;
      validateCount?: number;
      lastValidatedAt?: string;
      isUsed: boolean;
      isExpired: boolean;
      isCanceled: boolean;
      displayStatus: string;
      canCancel: boolean;
      paymentMethodSetupLink: string;
      sendChannel?: string;
      sendDestination?: string;
      sendActionCode?: string;
      sendUserId?: string;
      sendUserDisplayName?: string;
      sendAt?: string;
    }>>>(this.ApiUrl + 'api/invoice/GetPaymentMethodSetupCodes', request, { headers: headers });
  }

  getPaymentMethodSetupCodeValidateLogs(request: {
    pharmacyId: number;
    facilityId: number;
    patientId: number;
    setupCodeId: number;
  }): Observable<IResultObjVo<Array<{
    id: number;
    paymentMethodSetupCodeId: number;
    validateSequence: number;
    validatedAt: string;
    ipAddress?: string;
    userAgent?: string;
    success: boolean;
    errorMessage?: string;
  }>>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<Array<{
      id: number;
      paymentMethodSetupCodeId: number;
      validateSequence: number;
      validatedAt: string;
      ipAddress?: string;
      userAgent?: string;
      success: boolean;
      errorMessage?: string;
    }>>>(this.ApiUrl + 'api/invoice/GetPaymentMethodSetupCodeValidateLogs', request, { headers: headers });
  }

  lookupIpGeo(request: { ipAddress: string }): Observable<IResultObjVo<any>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<any>>(
      this.ApiUrl + 'api/invoice/LookupIpGeo',
      request,
      { headers: headers });
  }

  parseUserAgent(request: { userAgent: string }): Observable<IResultObjVo<{
    rawUserAgent?: string;
    deviceType?: string;
    device?: string;
    os?: string;
    osVersion?: string;
    browser?: string;
    browserVersion?: string;
  }>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<{
      rawUserAgent?: string;
      deviceType?: string;
      device?: string;
      os?: string;
      osVersion?: string;
      browser?: string;
      browserVersion?: string;
    }>>(this.ApiUrl + 'api/invoice/ParseUserAgent', request, { headers: headers });
  }

  cancelPaymentMethodSetupCode(request: {
    pharmacyId: number;
    facilityId: number;
    patientId: number;
    code: string;
  }): Observable<IResultObjVo<any>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<any>>(
      this.ApiUrl + 'api/invoice/CancelPaymentMethodSetupCode',
      request,
      { headers: headers });
  }

  logPaymentMethodSetupLinkCreate(request: {
    pharmacyId: number;
    facilityId: number;
    patientId: number;
    code?: string;
    paymentMethodSetupLink?: string;
    performedByUserId: string;
    success: boolean;
    errorMessage?: string;
  }): Observable<IResultObjVo<any>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<any>>(
      this.ApiUrl + 'api/invoice/LogPaymentMethodSetupLinkCreate',
      request,
      { headers: headers });
  }

  getPatientPaymentMethods(request: {
    pharmacyId: number;
    facilityId: number;
    patientId: number;
    sync?: boolean;
  }): Observable<IResultObjVo<{
    pharmacyId: number;
    patientId: number;
    found: boolean;
    customer?: {
      id: number;
      provider?: string;
      externalCustomerId?: string;
      contactName?: string;
      businessName?: string;
      cellPhone?: string;
      dateCreatedAtProvider?: string;
      dateUpdatedAtProvider?: string;
      syncedAtUtc?: string;
    };
    cards: Array<{
      id: number;
      provider?: string;
      externalCardId?: string;
      cardHolderName?: string;
      cardNumberMasked?: string;
      cardType?: string;
      cardExpiry?: string;
      isDefault: boolean;
      isActive: boolean;
      sequence?: number | null;
      dateCreatedAtProvider?: string;
      dateUpdatedAtProvider?: string;
      syncedAtUtc?: string;
    }>;
    bankAccounts: Array<{
      id: number;
      provider?: string;
      externalBankAccountId?: string;
      accountType?: string;
      accountCorporate?: string;
      isVerified: boolean;
      isReady: boolean;
      isActive: boolean;
      sequence?: number | null;
      bankIdNumber?: string;
      bankFullName?: string;
      bankShortName?: string;
      transitNumber?: string;
      routingNumber?: string;
      bankAccountNumberL4?: string;
      addressName?: string;
      addressCity?: string;
      addressProvince?: string;
      addressPostalCode?: string;
      addressEmail?: string;
      dateVerifiedAtProvider?: string;
      dateCreatedAtProvider?: string;
      dateUpdatedAtProvider?: string;
      syncedAtUtc?: string;
    }>;
    methodOrder?: Array<{
      sequence: number;
      methodTypeId: number;
      paymentCardId?: number | null;
      paymentBankAccountId?: number | null;
    }>;
    methodOrderUpdatedByStaffUserId?: string;
    methodOrderUpdatedByDisplayName?: string;
    methodOrderUpdatedByUserName?: string;
    methodOrderUpdatedAtUtc?: string;
  }>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<any>>(
      this.ApiUrl + 'api/invoice/GetPatientPaymentMethods',
      request,
      { headers: headers });
  }

  savePatientPaymentMethodOrder(request: {
    pharmacyId: number;
    facilityId: number;
    patientId: number;
    staffUserId: string;
    items: Array<{
      methodTypeId: number;
      paymentCardId?: number | null;
      paymentBankAccountId?: number | null;
    }>;
  }): Observable<IResultObjVo<any>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<any>>(
      this.ApiUrl + 'api/invoice/SavePatientPaymentMethodOrder',
      request,
      { headers: headers });
  }

  queryPatientPaymentMethodsSummary(request: {
    pharmacyId: number;
    patientIds: number[];
  }): Observable<IResultObjVo<Array<{
    patientId: number;
    found: boolean;
    cardCount: number;
    bankAccountCount: number;
  }>>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<Array<{
      patientId: number;
      found: boolean;
      cardCount: number;
      bankAccountCount: number;
    }>>>(this.ApiUrl + 'api/invoice/QueryPatientPaymentMethodsSummary', request, { headers: headers });
  }

  deletePatientPaymentCard(request: {
    pharmacyId: number;
    facilityId: number;
    patientId: number;
    paymentCustomerId: number;
    paymentMethodId: number;
    externalCustomerId?: string;
    externalCardId?: string;
    externalBankAccountId?: string;
    performedByUserId: string;
  }): Observable<IResultObjVo<any>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<any>>(
      this.ApiUrl + 'api/invoice/DeletePatientPaymentCard',
      request,
      { headers: headers });
  }

  deletePatientPaymentBankAccount(request: {
    pharmacyId: number;
    facilityId: number;
    patientId: number;
    paymentCustomerId: number;
    paymentMethodId: number;
    externalCustomerId?: string;
    externalCardId?: string;
    externalBankAccountId?: string;
    performedByUserId: string;
  }): Observable<IResultObjVo<any>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<any>>(
      this.ApiUrl + 'api/invoice/DeletePatientPaymentBankAccount',
      request,
      { headers: headers });
  }

  getPatientPaymentBankAccountPad(request: {
    paymentCustomerId: number;
    bankAccountId: number;
  }): Observable<IResultObjVo<any>> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<IResultObjVo<any>>(
      this.ApiUrl + 'api/invoice/GetPatientPaymentBankAccountPad',
      request,
      { headers: headers });
  }
}
